import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/feature/MovieCard";
import FilterSidebar, { type FilterSelection } from "@/pages/category/components/FilterSidebar";
import { allMovies, filterMovies, genreSlugToLabel, regionSlugToLabel } from "@/utils/movie";
import { sortOptions } from "@/mocks/site";
import { useSEO } from "@/hooks/useSEO";

const PAGE_SIZE = 10;

export default function Category() {
  const { type, filter } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const seeded = useRef(false);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (seeded.current) return;
    seeded.current = true;
    if (filter && !searchParams.get("genre")) {
      const next = new URLSearchParams(searchParams);
      next.set("genre", filter);
      setSearchParams(next, { replace: true });
    }
  }, [filter, searchParams, setSearchParams]);

  const selection: FilterSelection = useMemo(
    () => ({
      genres: (searchParams.get("genre") ?? "").split(",").filter(Boolean),
      regions: (searchParams.get("region") ?? "").split(",").filter(Boolean),
      years: (searchParams.get("year") ?? "").split(",").filter(Boolean),
    }),
    [searchParams]
  );

  const sort = searchParams.get("sort") ?? "latest";

  const results = useMemo(
    () => filterMovies(allMovies, { type, ...selection, sort }),
    [type, selection, sort]
  );

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [searchParams]);

  const titleText = type === "tv" ? "欧美电视剧" : type === "movie" ? "欧美电影" : "全部影视资源";

  useSEO({
    title:
      type === "movie"
        ? "欧美电影分类 - 欧美一级视频"
        : type === "tv"
          ? "欧美电视剧类别 - 欧美一级视频"
          : "影视分类 - 欧美一级视频",
    description:
      type === "movie"
        ? "浏览丰富的欧美电影资源，支持多维筛选与排序。"
        : "浏览丰富的欧美电视剧资源，支持多维筛选与排序。",
    keywords: `${titleText},欧美一级视频,${titleText}分类,欧美影视筛选`,
  });

  const updateParam = (key: string, values: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (values.length) next.set(key, values.join(","));
    else next.delete(key);
    setSearchParams(next);
  };

  const onToggle = (group: keyof FilterSelection, value: string) => {
    const list = selection[group];
    const nextValues = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
    const key = group === "genres" ? "genre" : group === "regions" ? "region" : "year";
    updateParam(key, nextValues);
  };

  const onClear = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("genre");
    next.delete("region");
    next.delete("year");
    setSearchParams(next);
  };

  const activeChips = [
    ...selection.genres.map((v) => ({ key: "genres" as const, value: v, label: genreSlugToLabel(v) ?? v })),
    ...selection.regions.map((v) => ({ key: "regions" as const, value: v, label: regionSlugToLabel(v) ?? v })),
    ...selection.years.map((v) => ({ key: "years" as const, value: v, label: `${v} 年` })),
  ];

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
      <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-xs text-foreground-600">
        <Link to="/" className="cursor-pointer hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/category/${type ?? "movie"}`} className="cursor-pointer hover:text-primary-600">
          {titleText}
        </Link>
        {filter ? (
          <>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900">{genreSlugToLabel(filter) ?? filter}</span>
          </>
        ) : null}
      </nav>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-black text-foreground-950 md:text-3xl">{titleText}</h1>
          <p className="text-sm text-foreground-600">
            共 <span className="font-semibold text-primary-600">{results.length}</span> 部作品 · 支持类型、地区与年份多维筛选
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-700 lg:hidden"
          >
            <i className="ri-filter-3-line"></i>
            筛选
            {activeChips.length > 0 ? (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-accent-500 px-1 text-[11px] font-semibold text-primary-950">
                {activeChips.length}
              </span>
            ) : null}
          </button>
          <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-1.5">
            <i className="ri-sort-desc text-base text-foreground-500"></i>
            <select
              value={sort}
              onChange={(e) => updateParam("sort", e.target.value === "latest" ? [] : [e.target.value])}
              className="cursor-pointer bg-transparent text-sm text-foreground-900 outline-none"
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <aside className="hidden w-64 shrink-0 lg:block">
          <FilterSidebar selection={selection} onToggle={onToggle} onClear={onClear} />
          <div className="mt-4 rounded-lg border border-background-200 bg-accent-50 p-4">
            <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-accent-900">
              <i className="ri-lightbulb-line"></i>
              使用提示
            </h3>
            <p className="text-xs leading-relaxed text-accent-800">
              可同时勾选多个类型、地区与年份，结果会实时刷新；点击作品卡片可查看完整资料。
            </p>
          </div>
        </aside>

        <div className="flex-1">
          {activeChips.length > 0 ? (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-600">当前筛选：</span>
              {activeChips.map((chip) => (
                <button
                  key={`${chip.key}-${chip.value}`}
                  type="button"
                  onClick={() => onToggle(chip.key, chip.value)}
                  className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-primary-100 px-2.5 py-1 text-xs text-primary-700 hover:bg-primary-200"
                >
                  {chip.label}
                  <i className="ri-close-line"></i>
                </button>
              ))}
              <button
                type="button"
                onClick={onClear}
                className="cursor-pointer whitespace-nowrap text-xs text-foreground-500 hover:text-foreground-800"
              >
                清除全部
              </button>
            </div>
          ) : null}

          {results.length === 0 ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                <i className="ri-movie-2-line text-3xl"></i>
              </span>
              <p className="text-base font-semibold text-foreground-900">暂无符合条件的作品</p>
              <p className="max-w-sm text-sm text-foreground-600">
                试试减少筛选条件，或浏览其他分类与热门内容。
              </p>
              <button
                type="button"
                onClick={onClear}
                className="mt-1 cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                重置筛选条件
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 xl:grid-cols-4">
                {results.slice(0, visible).map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              {visible < results.length ? (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="cursor-pointer whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50"
                  >
                    加载更多（剩余 {results.length - visible} 部）
                  </button>
                </div>
              ) : (
                <p className="mt-8 text-center text-xs text-foreground-500">已展示全部作品</p>
              )}
            </>
          )}
        </div>
      </div>

      {drawerOpen ? (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <div
            className="absolute inset-0 bg-background-950/50"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="absolute right-0 top-0 flex h-full w-[85%] max-w-sm flex-col overflow-y-auto bg-background-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-base font-bold text-foreground-950">筛选条件</span>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setDrawerOpen(false)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-600 hover:bg-background-100"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterSidebar selection={selection} onToggle={onToggle} onClear={onClear} />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-4 cursor-pointer rounded-md bg-primary-500 py-2.5 text-sm font-semibold text-background-50"
            >
              查看 {results.length} 部作品
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}